<template>
    <div
        class="WCode"
        :class="{ error }"
    >
        <Codemirror
            :id="id"
            ref="code"
            :value="value"
            :options="optionsCmp"
            :class="{ filled, focused, disabled }"
            v-on="inputListeners"
        />
        <Transition
            name="fade"
            :duration="500"
        >
            <WText
                v-if="error"
                class="Error"
                typo="span"
                size="10"
                color="red"
            >
                {{ error }}
            </WText>
        </Transition>
        <Transition
            name="fade"
            :duration="500"
        >
            <WText
                v-if="warningMessage"
                class="Warning"
                typo="span"
                size="10"
                color="yellow"
            >
                {{ warningMessage }}
            </WText>
        </Transition>
    </div>
</template>

<script>
/* eslint-disable no-restricted-syntax */
import VueTypes from 'vue-types'
import { merge, flatten } from 'lodash'
// Doc: https://github.com/surmon-china/vue-codemirror
import { codemirror as Codemirror } from 'vue-codemirror'
import 'codemirror/mode/javascript/javascript'
import 'codemirror/addon/lint/lint'
import 'codemirror/addon/lint/javascript-lint'
import { generateIdMixin } from '@/mixins'
import fetchPredefVariables from './fetchPredefVariables.service'

const DEFAULT_OPTIONS = {
    tabSize: 4,
    mode: {
        name: 'javascript',
        json: true,
        globalVars: true,
    },
    // theme: 'base16-dark',
    lineNumbers: true,
    line: true,
    gutters: ['CodeMirror-lint-markers'],
    lint: {
        options: {
            // https://jshint.com/docs/options/#esversion
            esversion: 6,
            // https://jshint.com/docs/options/#expr
            expr: true, // to let write a returned value directly instead of an assignment
            // https://jshint.com/docs/options/#asi
            asi: true, // supress warnings about missing semicolons
            // https://jshint.com/docs/options/#undef
            undef: false,
        },
    },
}

export default {
    name: 'WCode',
    components: {
        Codemirror,
    },
    mixins: [generateIdMixin],
    props: {
        error: VueTypes.string.def(''),
        options: VueTypes.any.def({}),
        disabled: VueTypes.bool.def(false),
        readonly: VueTypes.bool.def(false),
        value: VueTypes.oneOfType([String, Number]).isRequired,
        warningOptions: VueTypes.shape({
            // if an entityKey is present as withPredefVariables, it will check the variable names for this entity.
            withPredefVariables: VueTypes.string,
            // add warnings about undefined variables (but those included within "predef" array)
            undef: VueTypes.bool,
        }).loose,
    },
    data () {
        return {
            focused: false,
            predefVariables: undefined,
        }
    },
    computed: {
        inputListeners () {
            return merge({}, this.$listeners, {
                input: (value, e) => {
                    this.$emit('input', value, this.id)
                },
                focus: (options, event) => {
                    this.$emit('onFocus', event.target.value, this.id)
                    this.activeFocus()
                },
                blur: (evoptions, event) => {
                    this.$emit('onBlur', event.target.value, this.id)
                    this.deactiveFocus()
                },
            })
        },
        filled () {
            return (this.value || '').length > 0
        },
        // array of all the accepted variable names for linter
        predefVariableKeys () {
            const { predefVariables } = this
            if (predefVariables) {
                return [
                    ...predefVariables.functions,
                    ...predefVariables.dictionaryKeys,
                    ...predefVariables.transformKeys,
                    ...predefVariables.columnKeys,
                    ...Object.keys(predefVariables.relations),
                ]
            }
            return undefined
        },
        optionsCmp () {
            const {
                options, readonly, disabled, predefVariableKeys, warningOptions = {},
            } = this
            const opts = merge({}, DEFAULT_OPTIONS, options, { readOnly: readonly || disabled })
            if (Array.isArray(predefVariableKeys) && predefVariableKeys.length > 0) {
                // https://jshint.com/docs/options/#predef
                opts.lint.options.predef = predefVariableKeys
                // https://jshint.com/docs/options/#undef
                opts.lint.options.undef = true // add warnings about undefined variables (but those included within "predef" array)
            } else if (warningOptions?.undef === true) {
                // https://jshint.com/docs/options/#undef
                opts.lint.options.undef = true // add warnings about undefined variables
            }
            return opts
        },
        // checks every accepted variable name (if prop withPredefVariables has an entityKey)
        warningMessage () {
            const { value, warningOptions: { withPredefVariables: currentEntityKey } = {}, predefVariables } = this
            if (value && currentEntityKey && predefVariables) {
                const {
                    entityKeys, dictionaryKeys, transformKeys, functions, allowedJsWords, columnKeys,
                    relatedEntityNames, columnKeysByRelatedEntityName, relatedEntityNameByEntityKey,
                } = predefVariables
                // directVariables is an array of every variable (without a "." at the end, ie. without nested fields)
                const directVariables = value.match(/((?<![.\w\d"'`$])[a-zA-Z_$][a-zA-Z_$0-9]*(?![.\w\d"'`$]))/g) || []
                // variablesDeclaredWithinScope is an array of every variable declared directly within scope
                const variablesDeclaredWithinScope = (value.match(/((const)|(let)|(var)|(function)) ((?<![.\w\d"'`$])[a-zA-Z_$][a-zA-Z_$0-9]*(?![.\w\d"'`$]))/g) || [])
                    .map(str => str.replace(/^((const)|(let)|(var)|(function)) /, ''))
                // variablesDeclaredWithinScope is an array of every variable declared directly within scope using arrow functions.
                // Each arrow definition could have multiple arguments and detructurations, so... sorry for the regex...
                const variablesDeclaredWithinScopeUsingArrowFunctions = flatten(
                    (value.match(/\([.\s{[(]*((?<![\w\d"'`$])[a-zA-Z_$][a-zA-Z_$0-9,.\s:{}()[\]]*(?![.\w\d"'`$]))(\s*[)}\]]\s*)*=>/g) || [])
                        .map(str => str.replace(/[.(){}[\]:=>]/g, '').replace(/[\s,]+/g, ',').split(',').filter(x => x)),
                )
                const allowedVariableNames = [
                    ...variablesDeclaredWithinScope, ...variablesDeclaredWithinScopeUsingArrowFunctions,
                    ...dictionaryKeys, ...transformKeys, ...functions, ...allowedJsWords, ...columnKeys,
                ]
                const foundFunctions = []
                for (const variable of directVariables) {
                    if (functions.includes(variable)) {
                        foundFunctions.push(variable)
                    }
                    if (!allowedVariableNames.includes(variable)) {
                        if (relatedEntityNames.includes(variable)) {
                            // using a related entity name direcly (without a column key, see next loop for nestedVariables) is only allowed inside functions
                            if (foundFunctions.length === 0) {
                                return this.$t('error.codeLinterSuggestions.entityWithNoRelation', { code: variable })
                            }
                            return undefined
                        }
                        if (variable === currentEntityKey) {
                            return this.$t('error.codeLinterSuggestions.sameEntity', { code: variable })
                        }
                        if (relatedEntityNameByEntityKey[variable]) {
                            return this.$t(
                                'error.codeLinterSuggestions.mispelledRelatedName',
                                { code: variable, entity: variable, relation: relatedEntityNameByEntityKey[variable] },
                            )
                        }
                        if (entityKeys.includes(variable)) {
                            return this.$t('error.codeLinterSuggestions.notRelatedEntity', { code: variable, entityKey: variable })
                        }
                        return this.$t('error.codeLinterSuggestions.unknownVariable', { code: variable })
                    }
                }
                // nestedVariables is an array of every nested variables (with a ".", i.e. objects with a field). It's not recursive, only object name and field.
                const nestedVariables = value.match(/((?<![.\w\d"'`$])[a-zA-Z_$][a-zA-Z_$0-9]*\.[a-zA-Z_$0-9]+(?![.\w\d"'`$]))/g) || []
                for (const nestedVariable of nestedVariables) {
                    const [variable, field] = nestedVariable.split('.')
                    if (!allowedJsWords.includes(variable)) {
                        const columnKey = field
                        if (relatedEntityNames.includes(variable)) {
                            if (!columnKeysByRelatedEntityName[variable].includes(columnKey)) {
                                return this.$t('error.codeLinterSuggestions.entityWithUnknownColumn', { entityKey: variable, columnKey, code: nestedVariable })
                            }
                            return undefined
                        }
                        if (variable === currentEntityKey) {
                            return this.$t('error.codeLinterSuggestions.sameEntity', { code: nestedVariable })
                        }
                        if (relatedEntityNameByEntityKey[variable]) {
                            return this.$t(
                                'error.codeLinterSuggestions.mispelledRelatedName',
                                { code: nestedVariable, entity: variable, relation: relatedEntityNameByEntityKey[variable] },
                            )
                        }
                        if (entityKeys.includes(variable)) {
                            return this.$t('error.codeLinterSuggestions.notRelatedEntity', { code: nestedVariable, entityKey: variable })
                        }
                        return this.$t('error.codeLinterSuggestions.unknownVariableOrRelation', { code: nestedVariable, variable })
                    }
                }
            }
            return undefined
        },
    },
    created () {
        const { withPredefVariables: entityKey } = this.warningOptions || {}
        if (entityKey) {
            fetchPredefVariables(entityKey).then((data) => {
                const { relations } = data
                this.predefVariables = {
                    ...data,
                    relatedEntityNames: Object.keys(relations),
                    columnKeysByRelatedEntityName: Object.keys(relations).reduce((acc, name) => ({ ...acc, [name]: relations[name].columnKeys }), {}),
                    relatedEntityNameByEntityKey: Object.keys(relations).reduce((acc, name) => ({ ...acc, [relations[name].entityKey]: name }), {}),
                }
                // console.log('this.predefVariables', this.predefVariables)
            })
        }
    },
    methods: {
        activeFocus () {
            this.focused = true
        },
        deactiveFocus () {
            this.focused = false
        },
    },
}
</script>
<style lang="scss">
.CodeMirror-lint-tooltip {
	z-index: 10010 !important;
}
</style>

<style scoped lang="scss">
@import "@/theme/mixins/_global.scss";
@import '~codemirror/lib/codemirror.css';
@import '~codemirror/addon/lint/lint.css';
@import '~codemirror/addon/hint/show-hint.css';

.vue-codemirror {
    width: 100%;
    height: auto;
    resize: none;
    background: transparent;
    color: $gray-01;
    font-size: $form-font-size;
    font-weight: $form-font-weight;
    transition: border-color 0.5s ease-in-out;

    &:focus {
        border-color: $form-border-color-active;
        outline: none;
    }
    &[readonly] {
        cursor: not-allowed;
    }
    &::v-deep .CodeMirror {
        min-height: $form-height;
        height: auto;
        color: $form-color;
        border: $form-border;
        border-radius: 4px;
        background: $form-code-bg;
        font-size: $form-font-size;
        box-sizing: border-box;
    }
    &.disabled {
        &::v-deep .CodeMirror {
            background-color: $form-disabled-background;
        }
    }
}

.WCode {
    position: relative;
    &.error {
        textarea {
            opacity: .5;
        }
    }
}

.Error, .Warning {
    position: absolute;
    left: 5px;
}
</style>
